import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { SidebarContext } from '../../AppRoutes';
import { RiArrowRightSLine } from 'react-icons/ri';

// ====================================================================
// 🎯 COMPONENTE SUBMENU REFATORADO COM ESTILOS MODERNOS
// ====================================================================
const SubMenu = ({ item, onMobileMenuClose }) => {
  const [subnav, setSubnav] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showSimpleTooltip, setShowSimpleTooltip] = useState(false);
  const { sidebar } = useContext(SidebarContext);
  const itemRef = useRef(null);
  const tooltipRef = useRef(null);
  const simpleTooltipRef = useRef(null);
  const submenuContainerRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const hideSimpleTimeoutRef = useRef(null);
  const prevSubnavRef = useRef(false); // Rastreia estado anterior do subnav

  const showSubnav = () => setSubnav(!subnav);

  // ====================================================================
  // 🎯 AUTO-SCROLL: Quando submenu ABRE com sidebar expandido,
  //    rola para mostrar o máximo de subitens possível
  // ====================================================================
  useEffect(() => {
    // Detecta se foi uma transição de FECHADO → ABERTO
    const wasOpened = !prevSubnavRef.current && subnav;
    prevSubnavRef.current = subnav; // Atualiza o estado anterior

    // Só executa quando: submenu ACABOU DE ABRIR E sidebar está expandido E tem subitens
    if (!wasOpened || !sidebar || !item.subNav) return;

    // Aguarda a animação CSS terminar (350ms) antes de calcular posições
    const scrollTimeout = setTimeout(() => {
      const submenuContainer = submenuContainerRef.current;
      const menuItem = itemRef.current;

      if (!submenuContainer || !menuItem) return;

      // Encontra o container de navegação que tem o scroll
      const scrollContainer = menuItem.closest('.leftmenu-navigation-area');
      if (!scrollContainer) return;

      // Calcula posições após a animação terminar
      const scrollContainerRect = scrollContainer.getBoundingClientRect();
      const submenuRect = submenuContainer.getBoundingClientRect();
      const menuItemRect = menuItem.getBoundingClientRect();

      // Altura visível do container de scroll
      const visibleHeight = scrollContainerRect.height;

      // Verifica se o submenu está cortado na parte inferior OU superior
      const submenuBottomRelative = submenuRect.bottom - scrollContainerRect.top;
      const menuItemTopRelative = menuItemRect.top - scrollContainerRect.top;

      const isBottomCutOff = submenuBottomRelative > visibleHeight;
      const isTopCutOff = menuItemTopRelative < 0;

      // Se está cortado em alguma direção, precisa ajustar
      if (isBottomCutOff || isTopCutOff) {
        // Margem de segurança
        const topMargin = 10;

        // Posição absoluta do item no scroll (considerando scroll atual)
        const itemAbsoluteTop = menuItemRect.top - scrollContainerRect.top + scrollContainer.scrollTop;

        // Faz apenas UM scroll suave para a posição correta
        scrollContainer.scrollTo({
          top: itemAbsoluteTop - topMargin,
          behavior: 'smooth'
        });
      }
    }, 350); // Delay único após animação CSS (350ms)

    return () => clearTimeout(scrollTimeout);
  }, [subnav, sidebar, item.subNav]);

  // Função para detectar se estamos em mobile
  const isMobile = () => window.innerWidth <= 780;

  // Função para lidar com cliques em links - fecha o menu mobile se necessário
  const handleLinkClick = () => {
    if (isMobile() && onMobileMenuClose) {
      onMobileMenuClose();
    }
  };

  // Função genérica para posicionar tooltips
  const updateTooltipPosition = useCallback((tooltipElement) => {
    if (!itemRef.current || !tooltipElement || sidebar) return;

    // Usar requestAnimationFrame para garantir que o DOM esteja atualizado
    requestAnimationFrame(() => {
      if (!itemRef.current || !tooltipElement) return;

      const itemRect = itemRef.current.getBoundingClientRect();

      // Verificar se o elemento está visível
      if (itemRect.width === 0 && itemRect.height === 0) return;

      // Fazer o tooltip visível temporariamente para medir sua altura
      const originalDisplay = tooltipElement.style.display;
      const originalVisibility = tooltipElement.style.visibility;
      const originalOpacity = tooltipElement.style.opacity;

      tooltipElement.style.display = 'block';
      tooltipElement.style.visibility = 'hidden';
      tooltipElement.style.opacity = '0';

      const tooltipRect = tooltipElement.getBoundingClientRect();
      const tooltipHeight = tooltipRect.height;

      // Restaurar estilos originais
      tooltipElement.style.display = originalDisplay;
      tooltipElement.style.visibility = originalVisibility;
      tooltipElement.style.opacity = originalOpacity;

      const centerY = itemRect.top + (itemRect.height / 2);
      const windowHeight = window.innerHeight;

      let finalY = centerY;

      // Verificar se o tooltip sairia da parte superior da tela
      if (centerY - (tooltipHeight / 2) < 10) {
        finalY = (tooltipHeight / 2) + 10;
      }
      // Verificar se o tooltip sairia da parte inferior da tela
      else if (centerY + (tooltipHeight / 2) > windowHeight - 10) {
        finalY = windowHeight - (tooltipHeight / 2) - 10;
      }

      tooltipElement.style.top = `${finalY}px`;
    });
  }, [sidebar]);

  // Função para posicionar tooltip de submenu
  const updateSubmenuTooltipPosition = useCallback(() => {
    updateTooltipPosition(tooltipRef.current);
  }, [updateTooltipPosition]);

  // Função para posicionar tooltip simples
  const updateSimpleTooltipPosition = useCallback(() => {
    updateTooltipPosition(simpleTooltipRef.current);
  }, [updateTooltipPosition]);

  useEffect(() => {
    if (showTooltip && !sidebar && item.subNav) {
      // Múltiplas tentativas para garantir posicionamento correto
      const timeoutId1 = setTimeout(() => updateSubmenuTooltipPosition(), 10);
      const timeoutId2 = setTimeout(() => updateSubmenuTooltipPosition(), 50);
      const timeoutId3 = setTimeout(() => updateSubmenuTooltipPosition(), 100);
      
      const handleResize = () => {
        if (showTooltip && !sidebar) {
          updateSubmenuTooltipPosition();
        }
      };
      
      const handleScroll = () => {
        if (showTooltip && !sidebar) {
          updateSubmenuTooltipPosition();
        }
      };
      
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);
      
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }

    // Cleanup timeout quando componente desmonta
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [showTooltip, sidebar, item.subNav, updateSubmenuTooltipPosition]);

  useEffect(() => {
    if (showSimpleTooltip && !sidebar && !item.subNav) {
      // Múltiplas tentativas para garantir posicionamento correto
      const timeoutId1 = setTimeout(() => updateSimpleTooltipPosition(), 10);
      const timeoutId2 = setTimeout(() => updateSimpleTooltipPosition(), 50);
      const timeoutId3 = setTimeout(() => updateSimpleTooltipPosition(), 100);
      
      const handleResize = () => {
        if (showSimpleTooltip && !sidebar) {
          updateSimpleTooltipPosition();
        }
      };
      
      const handleScroll = () => {
        if (showSimpleTooltip && !sidebar) {
          updateSimpleTooltipPosition();
        }
      };
      
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);
      
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }

    // Cleanup timeout quando componente desmonta
    return () => {
      if (hideSimpleTimeoutRef.current) {
        clearTimeout(hideSimpleTimeoutRef.current);
      }
    };
  }, [showSimpleTooltip, sidebar, item.subNav, updateSimpleTooltipPosition]);

  const handleMouseEnter = () => {
    if (!sidebar) {
      if (item.subNav) {
        // Para itens com submenu
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        
        setShowTooltip(true);
        
        requestAnimationFrame(() => {
          updateSubmenuTooltipPosition();
        });
      } else {
        // Para itens sem submenu (tooltip simples)
        if (hideSimpleTimeoutRef.current) {
          clearTimeout(hideSimpleTimeoutRef.current);
          hideSimpleTimeoutRef.current = null;
        }
        
        setShowSimpleTooltip(true);
        
        requestAnimationFrame(() => {
          updateSimpleTooltipPosition();
        });
      }
    }
  };

  const handleMouseLeave = () => {
    if (item.subNav) {
      // Para itens com submenu
      hideTimeoutRef.current = setTimeout(() => {
        setShowTooltip(false);
      }, 33);
    } else {
      // Para itens sem submenu (tooltip simples)
      hideSimpleTimeoutRef.current = setTimeout(() => {
        setShowSimpleTooltip(false);
      }, 33);
    }
  };



  return (
    <>
      {/* ====================================================================
          ITEM PRINCIPAL DO MENU
          ==================================================================== */}
      <div
        ref={itemRef}
        className={`leftmenu-nav-item ${subnav && item.subNav ? 'leftmenu-nav-item--open' : ''}`}
        style={{ position: 'relative' }}
        data-has-submenu={item.subNav ? "true" : "false"}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Link 
          to={item.path || '#'} 
          className="leftmenu-nav-link"
          onClick={(e) => {
            if (item.subNav && sidebar) {
              showSubnav();
            } else if (item.path && item.path !== '#') {
              handleLinkClick();
            }
          }}
        >
          <div className="leftmenu-nav-link-content">
            <div className="leftmenu-nav-icon">
              {item.icon}
            </div>
            <span className="leftmenu-nav-label" title={item.title}>
              {item.title}
            </span>
          </div>
          {item.subNav && (
            <div className={`leftmenu-nav-arrow ${subnav ? 'leftmenu-nav-arrow--open' : ''}`}>
              <RiArrowRightSLine size={18} />
            </div>
          )}
        </Link>

              </div>

      {/* ====================================================================
          SUBITENS DO MENU (QUANDO EXPANDIDO)
          ==================================================================== */}
      {subnav && item.subNav && (
        <div
          ref={submenuContainerRef}
          className="leftmenu-submenu-container leftmenu-submenu-container--open"
        >
          {item.subNav.map((subItem, index) => (
            <div key={index} className="leftmenu-submenu-item">
              <Link
                to={subItem.path}
                className="leftmenu-submenu-link"
                onClick={handleLinkClick}
                title={subItem.title}
              >
                <div className="leftmenu-submenu-icon">
                  {subItem.icon}
                </div>
                <span className="leftmenu-nav-label" title={subItem.title}>
                  {subItem.title}
                </span>
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* ====================================================================
          TOOLTIP PORTAL - RENDERIZADO NO BODY VIA PORTAL
          ==================================================================== */}
      {!sidebar && item.subNav && createPortal(
        <div
          ref={tooltipRef}
          className={`leftmenu-collapsed-submenu-tooltip ${showTooltip ? 'leftmenu-collapsed-submenu-tooltip--visible' : ''}`}
          onMouseEnter={() => {
            // Cancelar timeout de hide quando mouse entra no tooltip
            if (hideTimeoutRef.current) {
              clearTimeout(hideTimeoutRef.current);
              hideTimeoutRef.current = null;
            }
            setShowTooltip(true);
            requestAnimationFrame(() => updateSubmenuTooltipPosition());
          }}
          onMouseLeave={() => {
            // Usar o mesmo timeout quando sai do tooltip
            hideTimeoutRef.current = setTimeout(() => {
              setShowTooltip(false);
            }, 33);
          }}
        >
          <div style={{
            padding: '8px 0 4px',
            borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
            marginBottom: '4px'
          }}>
            <div style={{
              padding: '0 20px',
              fontSize: '12px',
              fontWeight: '600',
              color: '#9ca3af',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {item.title}
            </div>
          </div>
          {item.subNav.map((subItem, index) => (
            <Link
              key={index}
              to={subItem.path}
              className="leftmenu-collapsed-submenu-item"
              onMouseEnter={() => {
                // Cancelar timeout quando mouse entra no item
                if (hideTimeoutRef.current) {
                  clearTimeout(hideTimeoutRef.current);
                  hideTimeoutRef.current = null;
                }
              }}
              onClick={() => {
                // Esconder tooltip imediatamente quando clica
                setShowTooltip(false);
                if (hideTimeoutRef.current) {
                  clearTimeout(hideTimeoutRef.current);
                  hideTimeoutRef.current = null;
                }
                // Fechar menu mobile se necessário
                handleLinkClick();
              }}
            >
              <div className="leftmenu-collapsed-submenu-icon">
                {subItem.icon}
              </div>
              {subItem.title}
            </Link>
          ))}
        </div>,
        document.body
      )}

      {/* ====================================================================
          TOOLTIP SIMPLES PARA ITENS SEM SUBMENU - VIA PORTAL
          ==================================================================== */}
      {!sidebar && !item.subNav && createPortal(
        <div
          ref={simpleTooltipRef}
          className={`leftmenu-collapsed-simple-tooltip ${showSimpleTooltip ? 'leftmenu-collapsed-simple-tooltip--visible' : ''}`}
          onMouseEnter={() => {
            // Cancelar timeout de hide quando mouse entra no tooltip
            if (hideSimpleTimeoutRef.current) {
              clearTimeout(hideSimpleTimeoutRef.current);
              hideSimpleTimeoutRef.current = null;
            }
            setShowSimpleTooltip(true);
            requestAnimationFrame(() => updateSimpleTooltipPosition());
          }}
          onMouseLeave={() => {
            // Usar o mesmo timeout quando sai do tooltip
            hideSimpleTimeoutRef.current = setTimeout(() => {
              setShowSimpleTooltip(false);
            }, 33);
          }}
        >
          {item.title}
        </div>,
        document.body
      )}
    </>
  );
};

export default SubMenu;
